import React, { useEffect, useContext, useState, useMemo } from "react";
import {
  CAccordion,
  CAccordionItem,
  CAccordionHeader,
  CAccordionBody,
} from "@coreui/react";
import api from "../services/config.url";
import showPopup from "../components/PopUp";
import AuthContext from "../contexts/auth";
import { downloadMateria } from "../services/downloadPdf";
import { setDownloadLoading } from "../services/downloadLoading";
import RevisionColorDot from "./RevisionColorDot";
import RevisionDateField from "./RevisionDateField";

const COLOR_RANK = { red: 0, yellow: 1, green: 2 };

const BooksAcquiredList = (props) => {
    const { cart, setLoading, setSizeCart, user } = useContext(AuthContext);
    const [orders, setOrders] = useState([]);
    const [revisionStatus, setRevisionStatus] = useState({});
    const sizeCart = useMemo(() => cart?.items?.length, [cart]);
    const [isLoadingContainer, setIsLoadingContainer] = useState(false);
    const isPremium = !!(
      user?.subscriptionExpiresAt && new Date(user.subscriptionExpiresAt) > new Date()
    );

    useEffect(() => {
      if (!cart) return;
      setSizeCart(sizeCart);
      setIsLoadingContainer(true);
      const fetchData = async () => {
        try {
          // Gatilho de criação automática das revisões (bolinha
          // preta+"?" inicial) — roda ao visitar "Meus Cadernos", no
          // lugar do antigo gatilho de "Meus Estudos".
          await api.get(`/customers/revisions-book/ensure`);

          const [ordersResponse, statusResponse] = await Promise.all([
            api.get(
              isPremium
                ? `/customers/products/full-catalog`
                : `/customers/orders/customer-products`
            ),
            api.get(`/customers/revisions-book/status`),
          ]);
          // Cadernos essenciais (SKU >= 4000) pertencem só à aba
          // "Cadernos Essenciais" (componente MyEbooks). Aqui, na aba
          // "Cadernos Jurídicos", ficam apenas os de SKU < 4000.
          const juridicos = (ordersResponse.data.items || []).filter((order) => {
            const skuNum = parseInt(order?.parent?.sku, 10);
            return isNaN(skuNum) || skuNum < 4000;
          });
          setOrders(juridicos);
          setRevisionStatus(statusResponse.data);
        } catch (error) {
          const BackendError = error?.response?.data?.message;
          const genericError = "Erro ao listar os cadernos adquiridos";
          showPopup(BackendError || genericError, "error");
        } finally {
          setIsLoadingContainer(false);
        }
      };
      fetchData();
    }, [cart, isPremium]);

    const handleDownloadCloud = async (obj) => {
        const sku = obj.sku || obj?.parent?.sku;
        const { name, secondName, document: documentCustomer } = user || {};

      if (!name) {
        showPopup(
          "O campo Nome é obrigatório! Acesse seu perfil para preenchê-lo.",
          "error"
        );
        return;
      }
      if (!documentCustomer) {
        showPopup(
          "O campo CPF é obrigatório! Acesse seu perfil para preenchê-lo.",
          "error"
        );
        return;
      }
      try {
        setDownloadLoading(true);
        const response = await api.post(
          `/customers/products/cloud-pdf`,
          {
            sku,
            customerName: [name, secondName].filter(Boolean).join(" "),
            customerCPF: documentCustomer,
          },
          { "Content-Type": "application/pdf" }
        );
        const pdfUrl = response.data.downloadUrl;
        // aguarda o download de fato antes de fechar o modal de loading
        const fileResponse = await fetch(pdfUrl);
        if (!fileResponse.ok) {
          throw new Error("Erro ao baixar o arquivo");
        }
        const blob = await fileResponse.blob();
        saveAs(blob, `${response.data.fileName}.pdf`);
      } catch (error) {
        const BackendError = error?.response?.data?.message;
        const genericError = "Erro ao baixar os arquivos";
        showPopup(BackendError || genericError, "error");
      } finally {
        setDownloadLoading(false);
      }
    };

  // Regra da matéria: se tiver subcadernos, baixa todos; senão, baixa a
  // própria matéria. Para não-premium, pula os que já foram baixados
  // (downloadedAt setado); premium sempre vem com downloadedAt null.
  const materiaAllDownloaded = (parent) => {
    const children = parent?.items || [];
    if (children.length > 0) return children.every((c) => c?.downloadedAt);
    return !!parent?.downloadedAt;
  };

  const handleMateriaDownload = async (parent) => {
    const children = parent?.items || [];
    if (children.length > 0) {
      const pending = children.filter((c) => !c?.downloadedAt);
      if (pending.length === 0) {
        showPopup(
          "Todos os cadernos desta matéria já foram baixados.",
          "alert"
        );
        return;
      }
    }
    // Uma chamada só → .zip (matéria com filhos) ou PDF simples (sem filhos).
    await downloadMateria(parent, user, setLoading, children);
  };

  const statusFor = (productId) =>
    revisionStatus[productId] || { revisionId: null, color: "unmarked", currentDate: null };

  const handleColorSelect = async (productId, newColor) => {
    const current = statusFor(productId);
    if (!current.revisionId) return;

    const previous = current;
    const shouldStampDate = !current.currentDate;
    const nextDate = shouldStampDate ? new Date() : current.currentDate;

    setRevisionStatus((prev) => ({
      ...prev,
      [productId]: { ...current, color: newColor, currentDate: nextDate },
    }));

    try {
      await api.patch(`/customers/revisions-book/${current.revisionId}/color`, {
        color: newColor,
      });
      // Data grava automaticamente na 1ª marcação (sem data ainda) —
      // depois disso, cor e data são totalmente independentes.
      if (shouldStampDate) {
        await api.patch(`/customers/revisions-book/${current.revisionId}/date`, {
          currentDate: nextDate,
        });
      }
    } catch (error) {
      setRevisionStatus((prev) => ({ ...prev, [productId]: previous }));
      const BackendError = error?.response?.data?.message;
      showPopup(BackendError || "Erro ao atualizar a marcação do caderno", "error");
    }
  };

  const handleDateSave = async (productId, newDate) => {
    const current = statusFor(productId);
    if (!current.revisionId) return;

    const previous = current;
    setRevisionStatus((prev) => ({
      ...prev,
      [productId]: { ...current, currentDate: newDate },
    }));

    try {
      await api.patch(`/customers/revisions-book/${current.revisionId}/date`, {
        currentDate: newDate,
      });
    } catch (error) {
      setRevisionStatus((prev) => ({ ...prev, [productId]: previous }));
      const BackendError = error?.response?.data?.message;
      showPopup(BackendError || "Erro ao atualizar a data", "error");
    }
  };

  // Linha de uma unidade marcável (caderno-filho, ou matéria de caderno
  // único agindo como sua própria unidade) — usada tanto no bloco solto
  // do topo quanto dentro do accordion normal.
  const renderLeafRow = (unit, { onDownload, downloaded, downloadLabel } = {}) => {
    const status = statusFor(unit._id);
    return (
      <div className="revision-row" key={unit._id}>
        <div className="revision-row-dot">
          <RevisionColorDot
            color={status.color}
            onSelect={(newColor) => handleColorSelect(unit._id, newColor)}
          />
        </div>
        <div className="revision-row-date">
          {status.color !== "unmarked" && (
            <RevisionDateField
              currentDate={status.currentDate}
              onSave={(newDate) => handleDateSave(unit._id, newDate)}
            />
          )}
        </div>
        <div className="revision-row-name">
          {unit.sku} - {unit.name}
        </div>
        <div className="revision-row-download">
          <input
            type="button"
            disabled={downloaded}
            onClick={onDownload}
            className="revision-download-btn btn btn-primary btnhover"
            value={downloaded ? "Já baixado" : downloadLabel || "Download"}
          />
        </div>
      </div>
    );
  };

  // --- Ordenação híbrida: cadernos marcados (cor ≠ unmarked) sobem pro
  // topo, misturando matérias, ordenados vermelho→amarelo→verde; os não
  // marcados ficam no accordion normal, agrupado por matéria, por SKU.
  const { topItems, groupedOrders } = useMemo(() => {
    const top = [];
    const grouped = [];

    orders.forEach((order) => {
      const parent = order.parent;
      const children = parent?.items || [];

      if (children.length > 0) {
        const remaining = [];
        children.forEach((child) => {
          const status = statusFor(child._id);
          if (status.color && status.color !== "unmarked") {
            top.push({ unit: child, parent });
          } else {
            remaining.push(child);
          }
        });
        grouped.push({ order, remaining });
      } else {
        const status = statusFor(parent._id);
        if (status.color && status.color !== "unmarked") {
          top.push({ unit: parent, parent, standalone: true });
        } else {
          grouped.push({ order, remaining: [] });
        }
      }
    });

    top.sort((a, b) => {
      const colorA = statusFor(a.unit._id).color;
      const colorB = statusFor(b.unit._id).color;
      const rankDiff = (COLOR_RANK[colorA] ?? 99) - (COLOR_RANK[colorB] ?? 99);
      if (rankDiff !== 0) return rankDiff;
      return (a.unit.sku || "").localeCompare(b.unit.sku || "");
    });

    return { topItems: top, groupedOrders: grouped };
  }, [orders, revisionStatus]);

  return (
    <>
   <CAccordion>
        <CAccordionItem itemKey={1} className="revision-outer-item">
          <div className="table-responsive revision-panel">
            <div className="table check-tbl">
              <CAccordionHeader className="bg-my-books-collapse">
                <div className="title-of-table ">
                  <div>MEUS CADERNOS</div>
                </div>
              </CAccordionHeader>
              <CAccordionBody className="p-0">
                <div className="revision-info-banner">
                  <span className="revision-info-banner-icon">i</span>
                  Clique na bolinha para fixar uma cor e marcar a leitura e a
                  relevância do caderno.
                </div>
                <div className="lines-of-table">
                  {orders && orders?.length < 1 ? (
                    !isLoadingContainer ? (
                      <div className="p-3">
                        Você não possui nenhum pedido com status de pagamento
                        aprovado.
                      </div>
                    ) : (
                      <div className="loadingContainer m-3"></div>
                    )
                  ) : (
                    <>
                      {topItems.length > 0 && (
                        <div className="revision-top-block">
                          {topItems.map(({ unit, parent, standalone }) =>
                            renderLeafRow(unit, {
                              onDownload: () =>
                                standalone
                                  ? handleMateriaDownload(parent)
                                  : handleDownloadCloud(unit),
                              downloaded: standalone
                                ? materiaAllDownloaded(parent)
                                : !!unit?.downloadedAt,
                              downloadLabel: standalone ? "Download" : "Download",
                            })
                          )}
                        </div>
                      )}
                      <CAccordion>
                        {groupedOrders.map(({ order, remaining }, index) => {
                          const parent = order.parent;
                          const isGroup = (parent?.items?.length || 0) > 0;

                          // Matéria de caderno único (sem filhos): é uma
                          // unidade marcável, então a bolinha vai numa linha
                          // simples (renderLeafRow) — não pode entrar dentro
                          // de CAccordionHeader, que já é um <button> do
                          // CoreUI (botão dentro de botão é HTML inválido e
                          // quebra o clique do popover).
                          if (!isGroup) {
                            return (
                              <React.Fragment key={index}>
                                {renderLeafRow(parent, {
                                  onDownload: () => handleMateriaDownload(parent),
                                  downloaded: materiaAllDownloaded(parent),
                                  downloadLabel: "Download",
                                })}
                              </React.Fragment>
                            );
                          }

                          return (
                            <CAccordionItem
                              itemKey={index}
                              key={index}
                              className="p-0"
                            >
                              <React.Fragment key={index}>
                                <CAccordionHeader>
                                  <div className="revision-materia-content">
                                    <div className="revision-materia-header-name">
                                      {parent?.name}
                                    </div>
                                    <input
                                      type="button"
                                      disabled={materiaAllDownloaded(parent)}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMateriaDownload(parent);
                                      }}
                                      className="revision-download-btn btn btn-primary btnhover"
                                      value={
                                        materiaAllDownloaded(parent)
                                          ? "Já baixado"
                                          : "Baixar tudo"
                                      }
                                    />
                                  </div>
                                </CAccordionHeader>
                                <CAccordionBody className="p-0">
                                  {remaining.length > 0 ? (
                                    <React.Fragment>
                                      {remaining.map((child) =>
                                        renderLeafRow(child, {
                                          onDownload: () => handleDownloadCloud(child),
                                          downloaded: !!child?.downloadedAt,
                                          downloadLabel: "Download",
                                        })
                                      )}
                                    </React.Fragment>
                                  ) : (
                                    <React.Fragment></React.Fragment>
                                  )}
                                </CAccordionBody>
                              </React.Fragment>
                            </CAccordionItem>
                          );
                        })}
                      </CAccordion>
                    </>
                  )}
                </div>
              </CAccordionBody>
            </div>
            <div></div>
          </div>
        </CAccordionItem>
      </CAccordion>
    </>
  );
};

export default BooksAcquiredList;
